/**
 * Reminder System Component
 *
 * Manage horse-related reminders (vaccinations, vet visits, blacksmith, etc.)
 * with dashboard statistics and creation modal.
 *
 * @package Lovasok_Marketplace
 */

import { LovasokAPI } from '../utils/api.js';
import { formatDate } from '../utils/formatters.js';

export default (config = {}) => ({
    reminders: [],
    horses: [],
    loading: true,
    filter: 'all',
    isModalOpen: false,
    newReminder: {
        horse_id: '',
        type: '',
        due_date: '',
        notes: ''
    },
    receiveEmails: config.receiveEmails ?? true,
    savingEmails: false,

    async saveEmailSettings() {
        this.savingEmails = true;
        try {
            const fd = new FormData();
            fd.append('action', 'lovasok_save_reminder_emails');
            fd.append('nonce', window.lovasokParams.ajaxNonce || window.lovasokParams.nonce);
            fd.append('receive_emails', this.receiveEmails ? '1' : '0');
            const response = await fetch(window.lovasokParams.ajaxUrl, { method: 'POST', body: fd });
            const result = await response.json();

            if (result && result.success !== false) {
                this.showToast(this.receiveEmails ? 'Email értesítők bekapcsolva.' : 'Email értesítők kikapcsolva.', 'success');
            } else {
                this.showToast('Nem sikerült menteni a beállítást.', 'error');
            }
        } catch (error) {
            console.error('Error saving email settings:', error);
            this.showToast('Hálózati hiba történt.', 'error');
        } finally {
            this.savingEmails = false;
        }
    },

    showToast(message, type = 'info') {
        const toast = document.createElement('div');
        toast.className = `fixed bottom-4 right-4 z-[9999] px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all transform translate-y-2 opacity-0 ${
            type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-600'
        }`;
        toast.textContent = message;
        document.body.appendChild(toast);
        requestAnimationFrame(() => toast.classList.remove('translate-y-2', 'opacity-0'));
        setTimeout(() => {
            toast.classList.add('translate-y-2', 'opacity-0');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    },

    // Computed: Filtered reminders
    get filteredReminders() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let results = this.reminders.filter(r => {
            if (this.filter === 'completed') {
                return this.isCompleted(r);
            }

            if (this.filter === 'upcoming') {
                if (this.isCompleted(r)) return false;
                const thirtyDaysFromNow = new Date();
                thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
                const dueDate = new Date(r.due_date);
                return dueDate <= thirtyDaysFromNow && dueDate >= today;
            }

            if (this.filter === 'overdue') {
                if (this.isCompleted(r)) return false;
                return new Date(r.due_date) < today;
            }

            // Type filters (vaccination, farrier, vet_checkup, …) — show all (active + completed)
            if (this.filter !== 'all') {
                return r.type === this.filter;
            }

            // 'all' — show every reminder (active + completed)
            return true;
        });

        // Sort: active overdue first → active upcoming → active future → completed
        results.sort((a, b) => {
            const aCompleted = this.isCompleted(a);
            const bCompleted = this.isCompleted(b);
            if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;
            return new Date(a.due_date) - new Date(b.due_date);
        });

        return results;
    },

    // Computed: Upcoming count (within 30 days)
    get upcomingCount() {
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        return this.reminders.filter(r => {
            if (this.isCompleted(r)) return false;
            const dueDate = new Date(r.due_date);
            return dueDate <= thirtyDaysFromNow && dueDate >= new Date(new Date().setHours(0,0,0,0));
        }).length;
    },

    // Computed: Overdue count
    get overdueCount() {
        const today = new Date();
        today.setHours(0,0,0,0);
        return this.reminders.filter(r => {
            if (this.isCompleted(r)) return false;
            return new Date(r.due_date) < today;
        }).length;
    },

    // Computed: Completed count (this year)
    get completedCount() {
        const thisYear = new Date().getFullYear();
        return this.reminders.filter(r => {
            if (!this.isCompleted(r)) return false;
            // Use due_date since completed_at is not in the schema
            const dateStr = r.completed_at || r.due_date;
            if (!dateStr) return false;
            return new Date(dateStr).getFullYear() === thisYear;
        }).length;
    },

    async init() {
        try {
            await Promise.all([
                this.fetchReminders(),
                this.fetchHorses()
            ]);
        } finally {
            this.$nextTick(() => document.getElementById('lmk-reminders-preload')?.remove());
        }
    },

    async fetchReminders() {
        this.loading = true;
        try {
            const data = await LovasokAPI.get('reminders');
            this.reminders = Array.isArray(data) ? data : (data.reminders || []);
            this.updateStore();
        } catch (error) {
            console.error('Error fetching reminders:', error);
        } finally {
            this.loading = false;
        }
    },

    async fetchHorses() {
        try {
            const data = await LovasokAPI.get('horses', {
                author: 'me',
                status: 'any',
                per_page: 100
            });
            this.horses = data.horses || [];
        } catch (error) {
            console.error('Error fetching horses:', error);
        }
    },

    openModal() {
        this.isModalOpen = true;
        this.newReminder = {
            horse_id: '',
            type: '',
            due_date: '',
            notes: ''
        };
    },

    closeModal() {
        this.isModalOpen = false;
    },

    async addReminder() {
        const normalizedDueDate = this.normalizeDueDate(this.newReminder.due_date);
        const horseId = Number(this.newReminder.horse_id || 0);

        if (!horseId || !this.newReminder.type || !normalizedDueDate) {
            alert('Kérlek töltsd ki a kötelező mezőket!');
            return;
        }

        try {
            await LovasokAPI.post('reminders', {
                horse_id: horseId,
                type: this.newReminder.type,
                due_date: normalizedDueDate,
                notes: this.newReminder.notes || ''
            });
            await this.fetchReminders();
            this.closeModal();
        } catch (error) {
            console.error('Error adding reminder:', error);
            alert('Hiba történt az emlékeztető létrehozása során.');
        }
    },

    async completeReminder(id) {
        if (!confirm('Biztosan befejezettnek jelölöd ezt az emlékeztetőt?')) return;

        try {
            await LovasokAPI.post(`reminders/${id}`, {
                is_completed: true
            });
            await this.fetchReminders();
        } catch (error) {
            console.error('Error completing reminder:', error);
            alert('Hiba történt az emlékeztető befejezése során.');
        }
    },

    async deleteReminder(id) {
        if (!confirm('Biztosan törölni szeretnéd ezt az emlékeztetőt?')) return;

        try {
            await LovasokAPI.delete(`reminders/${id}`);
            await this.fetchReminders();
        } catch (error) {
            console.error('Error deleting reminder:', error);
            alert('Hiba történt az emlékeztető törlése során.');
        }
    },

    // Get type label in Hungarian
    getTypeLabel(type) {
        const labels = {
            vaccination: 'Oltás',
            farrier: 'Patkolás',
            vet_checkup: 'Állatorvos',
            deworming: 'Féreghajtás',
            dental: 'Fogászat',
            competition: 'Verseny',
            training: 'Edzés',
            other: 'Egyéb'
        };
        return labels[type] || type;
    },

    // Get type icon (mock)
    getTypeIcon(type) {
        const icons = {
            vaccination: '💉',
            farrier: '🔨',
            vet_checkup: '🏥',
            deworming: '🧪',
            dental: '🦷',
            competition: '🏆',
            training: '🐎',
            other: '📋'
        };
        return icons[type] || '📋';
    },

    // Get type color class
    getTypeColor(type) {
        const colors = {
            vaccination: 'bg-blue-100 text-blue-800',
            farrier: 'bg-yellow-100 text-yellow-800',
            vet_checkup: 'bg-red-100 text-red-800',
            deworming: 'bg-emerald-100 text-emerald-800',
            dental: 'bg-purple-100 text-purple-800',
            competition: 'bg-orange-100 text-orange-800',
            training: 'bg-indigo-100 text-indigo-800',
            other: 'bg-gray-100 text-gray-800'
        };
        return colors[type] || colors.other;
    },

    // Get date color based on due date
    getDateColor(dateStr) {
        const dueDate = new Date(dateStr);
        const now = new Date();
        const daysUntil = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

        if (daysUntil < 0) {
            return 'text-red-600 font-semibold'; // Overdue
        } else if (daysUntil <= 7) {
            return 'text-orange-600 font-medium'; // Upcoming
        } else {
            return 'text-gray-600'; // Future
        }
    },

    // Days until due date
    daysUntil(dateStr) {
        const dueDate = new Date(dateStr);
        const now = new Date();
        const days = Math.floor((dueDate - now) / (1000 * 60 * 60 * 24));

        if (days < 0) {
            return `${Math.abs(days)} napja lejárt`;
        } else if (days === 0) {
            return 'Ma esedékes';
        } else if (days === 1) {
            return 'Holnap esedékes';
        } else if (days <= 7) {
            return `${days} nap múlva`;
        } else {
            return formatDate(dateStr);
        }
    },

    normalizeDueDate(rawDate) {
        if (!rawDate) return '';

        // Browser date input value (preferred)
        if (/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
            return rawDate;
        }

        // Hungarian localized input fallback: 1998. 09. 18.
        const huDateMatch = String(rawDate).match(/^(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})\.?$/);
        if (huDateMatch) {
            const year = huDateMatch[1];
            const month = huDateMatch[2].padStart(2, '0');
            const day = huDateMatch[3].padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        return '';
    },

    isCompleted(reminder) {
        const raw = reminder?.is_completed;
        return raw === true || raw === 1 || raw === '1';
    },

    updateStore() {
        if (typeof window.Alpine !== 'undefined' && window.Alpine.store('remindersApp')) {
            window.Alpine.store('remindersApp').upcoming = this.upcomingCount;
            window.Alpine.store('remindersApp').overdue = this.overdueCount;
        }
    }
});
